import {
  Button,
  CardActions,
  TextField,
  Tooltip,
  Zoom,
  LinearProgress,
  Chip,
  Stack,
  Box,
  Divider,
} from '@mui/material';
import { Card, CardContent, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { typographyTokens, semanticTypography } from '../../theme/typography';
import { UserProfileFields } from './UserProfileFields';
import { PrivilegeLevels } from '../../shared/constants/PrivilegeLevels';
import { SingleSelect } from '../Cells/CellTypes';
import MFAModal from './MFAModal';
import { updateUserMetaDataNonPIUserSettings } from '../../shared/functions/userFunctions';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import { DEFAULT_IS_EMAIL_NOTIFICATIONS_ENABLED } from '../../shared/constants/constants';
import PropTypes from 'prop-types';
import VerifyNewUserProfileEmailDialog from './VerifyNewUserProfileEmailDialog';
import { isLoggedInUserAnIdpUser } from '../../utils/ssoUtils';
import jwtDecode from 'jwt-decode';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  cancelButton: {
    backgroundColor: theme.palette.toneNegative.main,
    color: theme.palette.whiteScaleLighter.main,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1vw',
  },
  card: {
    minWidth: 500,
    maxWidth: 560,
  },
  title: {
    ...semanticTypography.modalTitle,
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  actions: {
    marginBottom: '1rem',
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '0.75rem',
  },
  section: {
    padding: '0.85rem 0.85rem 1.25rem',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.whiteScale.main}`,
    backgroundColor: theme.palette.whiteScaleLight.main,
  },
  sectionGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.55rem',
  },
  fieldBlock: {
    display: 'grid',
    gridTemplateColumns: '140px minmax(0, 1fr)',
    columnGap: '0.75rem',
    alignItems: 'center',
    padding: '0.4rem 0.5rem',
    border: `1px solid ${theme.palette.whiteScale.main}`,
    borderRadius: '8px',
    backgroundColor: theme.palette.whiteScaleLighter.main,
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.whiteScaleDarker.contrastText,
    lineHeight: 1.2,
    padding: '0.2rem 0.35rem 0.2rem 0.65rem',
    textTransform: 'uppercase',
    textAlign: 'left',
  },
  fieldValue: {
    color: theme.palette.whiteScaleDarker.contrastText,
    fontSize: '0.9rem',
    lineHeight: 1.35,
  },
  fieldValueContainer: {
    minWidth: 0,
    padding: '0.15rem 0.2rem 0.15rem 0.35rem',
    width: '100%',
  },
  mfaChipContainer: {
    marginTop: '4px',
    marginBottom: '6px',
  },
  centeredContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
}));
export default function ProfileComponent({
  user,
  setUser,
  editingUserPrivilege,
  updateUserMutation,
  autoDisableNotifications = false,
  isOnProfilePage = false,
}) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [userInfo, setUserInfo] = useState(user);
  const [openDialog, setOpenDialog] = useState(false);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);

  const loggedInUserIdToken = useSelector((state) => state.user.idToken);
  let isIdpUser = false;
  try {
    if (typeof loggedInUserIdToken === 'string' && loggedInUserIdToken.length > 0) {
      isIdpUser = isLoggedInUserAnIdpUser(jwtDecode(loggedInUserIdToken));
    }
  } catch {
    isIdpUser = false;
  }

  const dispatch = useDispatch();

  const [isEmailNotificationsEnabled, setIsEmailNotificationsEnabled] = useState(
    userInfo.nonPIUserSettings?.isEmailNotificationOn ?? DEFAULT_IS_EMAIL_NOTIFICATIONS_ENABLED
  );

  useEffect(() => {
    setIsEmailNotificationsEnabled(
      userInfo.nonPIUserSettings?.isEmailNotificationOn ?? DEFAULT_IS_EMAIL_NOTIFICATIONS_ENABLED
    );
  }, [userInfo]);

  //get only user, manager, admin
  const privilegeLevelsForSelect = Object.values(PrivilegeLevels)
    .filter((level) => level.level !== undefined && level.level <= editingUserPrivilege)
    .map((level) => ({ title: level.title, value: level.level }));

  const updateSelfUserInfo = async () => {
    try {
      await updateUserMutation.mutateAsync(userInfo);

      if (user.email !== userInfo.email) {
        dispatch(
          openSnackbar({
            message: `Successfully updated profile, Please verify your new email`,
            severity: 'success',
          })
        );
        // proceed to verify email dialog
        setOpenDialog(true);
      } else {
        dispatch(
          openSnackbar({
            message: `Successfully updated profile`,
            severity: 'success',
          })
        );
        setUser();
      }
    } catch (err) {
      console.error(err);
      dispatch(
        openSnackbar({
          message: `Failed to update profile`,
          severity: 'error',
        })
      );
    }
  };

  const updateOtherUserInfo = async () => {
    try {
      const res = await updateUserMutation.mutateAsync(userInfo);
      if (res.username) {
        setUser(userInfo);
      } else {
        setUser(user);
        setUserInfo(user);
      }

      dispatch(
        openSnackbar({
          message: `Successfully updated profile`,
          severity: 'success',
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        openSnackbar({
          message: `Failed to update profile`,
          severity: 'error',
        })
      );
    }
  };

  const onSave = (e) => {
    if (isOnProfilePage) {
      updateSelfUserInfo();
    } else {
      updateOtherUserInfo();
    }
  };

  const getFriendlyMfaMethod = (method) => {
    if (method === 'SMS_MFA') return 'Text message (SMS)';
    if (method === 'SOFTWARE_TOKEN_MFA') return 'Authenticator app';
    if (method === 'Not Configured') return 'Not Configured';
    return method;
  };

  const renderMfaSettings = (value, dataCy) => {
    if (!value || value === 'Not Configured') {
      return (
        <div className={classes.centeredContent}>
          <Typography className={classes.fieldValue} data-cy={dataCy}>
            Not Configured
          </Typography>
        </div>
      );
    }

    const methods = Array.isArray(value) ? value : [value];
    const friendlyMethods = methods.map((method) => getFriendlyMfaMethod(method));

    return (
      <div className={classes.centeredContent}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          className={classes.mfaChipContainer}
          data-cy={dataCy}
        >
          {friendlyMethods.map((method) => (
            <Chip key={method} label={method} size="small" />
          ))}
        </Stack>
      </div>
    );
  };

  const onClickChangeEmailNotificationsSettingButton = async () => {
    try {
      await updateUserMetaDataNonPIUserSettings(userInfo.id, {
        nonPIUserSettings: { isEmailNotificationOn: !isEmailNotificationsEnabled },
      });
      setIsEmailNotificationsEnabled(!isEmailNotificationsEnabled);
      dispatch(
        openSnackbar({
          message: `Successfully ${isEmailNotificationsEnabled ? 'disabled' : 'enabled'} email notifications`,
          severity: 'success',
        })
      );
    } catch (err) {
      dispatch(
        openSnackbar({
          message: `Failed to ${isEmailNotificationsEnabled ? 'disable' : 'enable'} email notifications`,
          severity: 'error',
        })
      );
    }
  };

  useEffect(() => {
    setIsEditing(false);
    setUserInfo(user);
  }, [user]);

  useEffect(() => {
    if (autoDisableNotifications) {
      if (isEmailNotificationsEnabled) {
        updateUserMetaDataNonPIUserSettings(userInfo.id, {
          nonPIUserSettings: { isEmailNotificationOn: false },
        })
          .then(() => {
            setIsEmailNotificationsEnabled(false);
            dispatch(
              openSnackbar({
                message: `Successfully disabled email notifications`,
                severity: 'success',
              })
            );
          })
          .catch((err) => {
            dispatch(
              openSnackbar({
                message: `Failed to ${isEmailNotificationsEnabled ? 'disable' : 'enable'} email notifications`,
                severity: 'error',
              })
            );
          });
      } else {
        dispatch(
          openSnackbar({
            message: `Email notifications has already been disabled`,
            severity: 'success',
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDisableNotifications]);

  const renderActions = () => {
    if (updateUserMutation.isPending) {
      return <LinearProgress />;
    }
    return (
      <CardActions style={{ justifyContent: 'center' }}>
        {isEditing ? (
          <div>
            <Button
              variant="contained"
              color="primary"
              style={{ marginRight: '7px' }}
              onClick={(e) => {
                setIsEditing(false);
                onSave(e);
              }}
            >
              SAVE
            </Button>
            <Button
              className={classes.cancelButton}
              variant="contained"
              onClick={(e) => {
                setUserInfo(user);
                setIsEditing(false);
              }}
            >
              CANCEL
            </Button>
          </div>
        ) : (
          <>
            <Button variant="outlined" color="primary" onClick={() => setIsEditing(true)}>
              Edit Info
            </Button>
            <Button variant="outlined" color="primary" onClick={onClickChangeEmailNotificationsSettingButton}>
              {`${isEmailNotificationsEnabled ? 'Disable' : 'Enable'} Email Notifications`}
            </Button>
          </>
        )}
        {isOnProfilePage && !isEditing && (
          <Tooltip
            title={
              <Typography variant={'body2'}>
                {
                  'In-app configuration of MFA is only applicable to username/password logins. To enable MFA on SSO logins, please configure it with your SSO provider'
                }
              </Typography>
            }
            TransitionComponent={Zoom}
            enterDelay={200}
            placement={'right'}
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setIsMfaModalOpen(true)}
                data-cy="configMfaButton-ProfileComponent"
                disabled={isIdpUser}
              >
                Configure MFA
              </Button>
            </span>
          </Tooltip>
        )}
      </CardActions>
    );
  };

  const shouldRenderField = (field) =>
    userInfo[field.field] || userInfo[field.field] === '' || (userInfo[field.field] === 0 && editingUserPrivilege > 0);

  const profileFieldGroups = [
    {
      key: 'identity',
      title: 'Identity',
      fields: ['username', 'name', 'email'],
    },
    {
      key: 'access',
      title: 'Access',
      fields: ['privilege', 'enabled'],
    },
    {
      key: 'security',
      title: 'Security',
      fields: ['mfaEnabled', 'mfaSettings', 'preferredMfaMethod', 'phone'],
    },
  ];

  const groupedFields = profileFieldGroups
    .map((group) => ({
      ...group,
      fields: UserProfileFields.filter((field) => group.fields.includes(field.field)).filter(shouldRenderField),
    }))
    .filter((group) => group.fields.length > 0);

  const renderedFieldKeys = new Set(groupedFields.flatMap((group) => group.fields.map((field) => field.field)));
  const additionalFields = UserProfileFields.filter((field) => !renderedFieldKeys.has(field.field)).filter(
    shouldRenderField
  );

  if (additionalFields.length > 0) {
    groupedFields.push({
      key: 'additional',
      title: 'Additional',
      fields: additionalFields,
    });
  }

  const renderField = (field) => {
    let userInfoFieldComponent;
    const editFieldSx = {
      width: '100%',
      '& .MuiInputBase-input': {
        fontSize: '0.9rem',
        textAlign: 'center',
      },
      '& .MuiInputBase-root': {
        width: '100%',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem',
      },
      '& .MuiSelect-select': {
        fontSize: '0.9rem',
        textAlign: 'center',
      },
    };

    let isEditable = isEditing && field.editable && field.minClearance <= editingUserPrivilege;
    const isPrivilegeField = field.field === 'privilege';
    const isEmailField = field.field === 'email';

    if (isOnProfilePage && isEmailField) {
      isEditable = isEditing && field.editable && PrivilegeLevels.user.level <= editingUserPrivilege && !isIdpUser;
    }

    if (isEditable && isPrivilegeField) {
      userInfoFieldComponent = (
        <SingleSelect
          callBack={(e) => {
            setUserInfo({
              ...userInfo,
              [field.field]: e.target.value,
            });
          }}
          items={privilegeLevelsForSelect}
          value={userInfo[field.field]}
          label={'Privilege Level'}
          data-cy={field.dataCy}
          variant="standard"
          sx={editFieldSx}
          fullWidth
        />
      );
    } else if (isEditable) {
      userInfoFieldComponent = (
        <TextField
          variant="standard"
          sx={editFieldSx}
          fullWidth
          value={userInfo[field.field]}
          onChange={(e) => {
            setUserInfo({
              ...userInfo,
              [field.field]: e.target.value,
            });
          }}
          data-cy={field.dataCy}
        />
      );
    } else if (isPrivilegeField) {
      const privilegeOption = privilegeLevelsForSelect.find((level) => level.value === userInfo[field.field]);
      userInfoFieldComponent = (
        <Typography className={classes.fieldValue} data-cy={field.dataCy}>
          {privilegeOption?.title ?? userInfo[field.field]}
        </Typography>
      );
    } else if (field.field === 'mfaSettings') {
      userInfoFieldComponent = renderMfaSettings(userInfo[field.field], field.dataCy);
    } else if (field.field === 'preferredMfaMethod') {
      userInfoFieldComponent = (
        <Typography className={classes.fieldValue} data-cy={field.dataCy}>
          {getFriendlyMfaMethod(userInfo[field.field] || 'Not Configured')}
        </Typography>
      );
    } else {
      userInfoFieldComponent = (
        <Typography className={classes.fieldValue} data-cy={field.dataCy}>
          {userInfo[field.field]}
        </Typography>
      );
    }

    return (
      <Box key={field.field} className={classes.fieldBlock}>
        <Typography className={classes.fieldLabel}>{field.label}</Typography>
        <Box className={classes.fieldValueContainer}>{userInfoFieldComponent}</Box>
      </Box>
    );
  };

  return (
    userInfo && (
      <>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h4" component="div" className={classes.title}>
              {user.name}'s Profile
            </Typography>
            <Box className={classes.actions}>{renderActions()}</Box>
            <Divider />
            <Box className={classes.detailsContainer}>
              {groupedFields.map((group) => (
                <Box key={group.key} className={classes.section}>
                  <Typography sx={{ ...typographyTokens.eyebrowText, mb: '0.75rem', textAlign: 'center' }}>
                    {group.title}
                  </Typography>
                  <Box className={classes.sectionGrid}>{group.fields.map((field) => renderField(field))}</Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
        {isOnProfilePage && (
          <VerifyNewUserProfileEmailDialog
            userId={user.id}
            openDialog={openDialog}
            setUser={setUser}
            handleDialogClose={() => setOpenDialog(false)}
          />
        )}
        {isOnProfilePage && (
          <MFAModal
            isModalOpen={isMfaModalOpen}
            setIsModalOpen={setIsMfaModalOpen}
            user={userInfo}
            setUserInfo={setUserInfo}
            data-cy="mfaModal-ProfileComponent"
          />
        )}
      </>
    )
  );
}

ProfileComponent.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  setUser: PropTypes.func.isRequired,
  updateUserMutation: PropTypes.func.isRequired,
  autoDisableNotifications: PropTypes.bool,
  isOnProfilePage: PropTypes.bool,
};
